'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Menu, X } from 'lucide-react';
import { GoogleReviewsSection } from './components/GoogleReviewsSection';
import { useStoreStatus } from '@/hooks/useStoreStatus';
import { DEFAULT_SCHEDULE, formatScheduleLines } from '@/lib/storeSchedule';
import './landing.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function rebuildBloom(count: number, field: HTMLElement | null) {
  if (!field) return;
  field.innerHTML = '';
  for (let i = 0; i < count; i++) {
    const p = document.createElement('div');
    p.className = 'bloom-petal';
    const size = 14 + Math.random() * 28;
    const dur = 11 + Math.random() * 16;
    const dur2 = 3 + Math.random() * 5;
    const delay = -Math.random() * dur;
    const x = Math.random() * 100;
    const opacity = 0.18 + Math.random() * 0.35;
    const sway = (Math.random() < 0.5 ? -1 : 1) * (40 + Math.random() * 80);
    p.style.setProperty('--ps', size + 'px');
    p.style.setProperty('--pd', dur + 's');
    p.style.setProperty('--pd2', dur2 + 's');
    p.style.setProperty('--pdl', delay + 's');
    p.style.setProperty('--px', x + '%');
    p.style.setProperty('--po', String(opacity));
    p.style.setProperty('--pmx', sway + 'px');
    field.appendChild(p);
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SCENE_IDS = ['s0', 's1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9'];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const bloomFieldRef = useRef<HTMLDivElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { status: storeStatus } = useStoreStatus();
  const scheduleLines = formatScheduleLines(storeStatus?.schedule ?? DEFAULT_SCHEDULE, 'ro');

  // Mark landing page (used to hide the shared site header/footer)
  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => {
      document.documentElement.classList.remove('landing-page');
      delete document.body.dataset.mood;
      delete document.body.dataset.voice;
      document.body.style.removeProperty('--bloom-opacity');
    };
  }, []);

  // Apply default atmosphere to body
  useEffect(() => {
    document.body.dataset.mood = 'romantic';
    document.body.dataset.voice = 'florist';
    document.body.style.setProperty('--bloom-opacity', '1');
    rebuildBloom(33, bloomFieldRef.current);
  }, []);

  // Reveal each scene's entrance animation as it scrolls into view
  useEffect(() => {
    const scenes = SCENE_IDS
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealing', 'active');
          }
        });
      },
      { threshold: 0.35 }
    );

    scenes.forEach((scene) => observer.observe(scene));

    // Reveal the first scene immediately on load
    if (scenes[0]) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        scenes[0].classList.add('is-revealing', 'active');
      }));
    }

    return () => observer.disconnect();
  }, []);

  const seekTo = (idx: number) => {
    document.getElementById(SCENE_IDS[idx])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const seekToAndClose = (idx: number) => {
    seekTo(idx);
    setMobileMenuOpen(false);
  };

  const d = (ms: string) => ({ '--d': ms } as React.CSSProperties);

  return (
    <>
      {/* Bloom field */}
      <div className="bloom-field" id="bloomField" ref={bloomFieldRef} aria-hidden="true" />

      {/* Landing nav */}
      <nav>
        <Link className="nav-logo" href="/">
          <Image
            src="https://res.cloudinary.com/dm7ttgpta/image/upload/v1769631342/Logo_Simte_Poezia_Florilor_V2_Negru_d4urcr.png"
            alt="Buchetul Simonei"
            width={120}
            height={48}
            className="object-contain"
            priority
          />
        </Link>
        <ul className="nav-links">
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekTo(2); }}>Despre</a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekTo(4); }}>Servicii</a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekTo(8); }}>Evenimente</a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekTo(9); }}>Contact</a></li>
        </ul>
        <Link className="nav-cta" href="/shop">Comandă Acum</Link>
        <button
          type="button"
          className="nav-menu-btn"
          aria-label={mobileMenuOpen ? 'Închide meniul' : 'Deschide meniul'}
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile nav panel */}
      <div className={`nav-mobile-panel ${mobileMenuOpen ? 'is-open' : ''}`}>
        <ul className="nav-mobile-links">
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekToAndClose(2); }}>Despre</a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekToAndClose(4); }}>Servicii</a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekToAndClose(8); }}>Evenimente</a></li>
          <li><a href="#" onClick={(e) => { e.preventDefault(); seekToAndClose(9); }}>Contact</a></li>
        </ul>
        <Link className="nav-mobile-cta" href="/shop" onClick={() => setMobileMenuOpen(false)}>
          Comandă Acum
        </Link>
      </div>

      {/* Scenes */}
      <div id="stage">

        {/* 01 INTRO */}
        <div className="scene" id="s0">
          <div className="scene-bg intro-bg" />
          <div className="scene-bg intro-petal" id="petal0">
            <Image
              src="https://res.cloudinary.com/dm7ttgpta/image/upload/v1764067790/a631f8ec-b16b-402a-9566-67749f6b33e5/il75wd9vjs0ee5uqzyce.webp"
              alt=""
              fill
              priority
              sizes="(max-width: 900px) 32vw, 480px"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>
          <div className="intro-content">
            <div className="intro-eyebrow-row">
              <span className="eyebrow fade-up" style={d('200ms')}>Florărie premium · 2026</span>
              <span className="intro-meta fade-up" style={d('300ms')}>Tamaseni · Neamț · Ro</span>
            </div>
            <h1 className="intro-title lines">
              <span className="line"><span style={d('0ms')}>Buchetul</span></span>
              <span className="line"><span style={d('200ms')}><em>Simonei</em></span></span>
            </h1>
            <div className="intro-bottom">
              <p className="intro-tag fade-up" style={d('600ms')}>
                Povești scrise în petale,<br />emoții transmise prin flori.
              </p>
            </div>
          </div>
          <div className="scroll-hint fade-up" style={d('800ms')}>
            <span>Glisează în jos</span>
            <div className="scroll-hint-line" />
          </div>
        </div>

        {/* 02 BIG WORD */}
        <div className="scene" id="s1">
          <div className="word-stage">
            <span className="word-mark fade-up" style={d('0ms')}>Capitolul I</span>
            <h2 className="word-mega letters" id="mega1">
              {['p','o','e','z','i','a',' ','f','l','o','r','i','l','o','r','.'].map((l, i) => (
                <span key={i} className="l" style={{ '--d': `${i * 50}ms` } as React.CSSProperties}>
                  {l === ' ' ? ' ' : l}
                </span>
              ))}
            </h2>
            <p className="word-sub fade-up" style={d('700ms')}>
              Fiecare petală, fiecare nuanță, fiecare buchet — o poveste spusă fără cuvinte.
            </p>
          </div>
        </div>

        {/* 03 STORY */}
        <div className="scene" id="s2">
          <div className="story-grid">
            <div className="story-img-wrap fade-up" style={d('200ms')}>
              <Image
                src="https://res.cloudinary.com/dm7ttgpta/image/upload/v1763577654/a425b4f7-bfce-46f5-8e0f-f4241d3131b1/vszdn1n6lpsjbmlzt198.webp"
                alt="Flori proaspete"
                fill
                sizes="(max-width: 900px) 100vw, 45vw"
                style={{ objectFit: 'cover' }}
              />
              <div className="story-img-tag">Est. 2026</div>
            </div>
            <div className="story-text">
              <span className="eyebrow fade-up" style={d('0ms')}>Povestea noastră</span>
              <h2 className="story-title lines">
                <span className="line"><span style={d('100ms')}>Creat cu</span></span>
                <span className="line"><span style={d('280ms')}><em className="italic-rose">pasiune</em>,</span></span>
                <span className="line"><span style={d('460ms')}>livrat cu inimă.</span></span>
              </h2>
              <p className="story-body fade-up" style={d('700ms')}>
                La Buchetul Simonei, credem că fiecare floare spune o poveste. De la o mică afacere locală la un nume de încredere — echipa noastră lucrează cu atenție pentru a crea buchete care reflectă emoțiile și ocaziile speciale ale clienților noștri.
              </p>
              <p className="story-sig fade-up" style={d('850ms')}>— Simona</p>
            </div>
          </div>
        </div>

        {/* 04 BIG QUOTE */}
        <div className="scene" id="s3">
          <div className="quote-stage">
            <span className="quote-mark fade-up" style={d('0ms')}>&ldquo;</span>
            <p className="quote-text lines">
              <span className="line"><span style={d('100ms')}>Florile vorbesc o limbă</span></span>
              <span className="line"><span style={d('280ms')}>pe care o înțelege</span></span>
              <span className="line"><span style={d('460ms')}><em>doar inima</em>.</span></span>
            </p>
            <p className="quote-attr fade-up" style={d('900ms')}>— Floraria Buchetul Simonei</p>
          </div>
        </div>

        {/* 05 SERVICII */}
        <div className="scene" id="s4">
          <div className="srv-stage">
            <div className="srv-head">
              <h2 className="srv-title lines">
                <span className="line"><span style={d('0ms')}>L&apos;Art</span></span>
                <span className="line"><span style={d('180ms')}>des <em>Fleurs</em>.</span></span>
              </h2>
              <p className="srv-intro fade-up" style={d('400ms')}>
                Trei moduri prin care transformăm florile în emoție — alege experiența potrivită pentru momentul tău.
              </p>
            </div>
            <div className="srv-grid">
              {[
                {
                  delay: '500ms',
                  img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1764067790/a631f8ec-b16b-402a-9566-67749f6b33e5/il75wd9vjs0ee5uqzyce.webp',
                  alt: 'Buchete Romantice', num: '01',
                  title: 'Buchete Romantice',
                  desc: 'Aranjamente pentru momentele care merită să fie reținute. Trandafiri, bujori și flori de sezon.',
                  cta: 'Vezi colecția',
                },
                {
                  delay: '650ms',
                  img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1763577654/a425b4f7-bfce-46f5-8e0f-f4241d3131b1/vszdn1n6lpsjbmlzt198.webp',
                  alt: 'Flori Proaspete', num: '02',
                  title: 'Flori Proaspete',
                  desc: 'Calitate premium direct de la cultivatori, livrate în maxim 24h pentru a-ți păstra prospețimea.',
                  cta: 'Descoperă',
                },
                {
                  delay: '800ms',
                  img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1763581406/5da3f391-0d05-4fd9-a7d2-fb1a98614479/kc9bod1ii9pm2najso7s.webp',
                  alt: 'Design Personalizat', num: '03',
                  title: 'Design Personalizat',
                  desc: 'Aranjamente unice după preferințele tale — pentru evenimente, cadouri sau spații speciale.',
                  cta: 'Comandă',
                },
              ].map((card) => (
                <Link key={card.num} href="/shop" className="srv-card fade-up" style={d(card.delay)}>
                  <div className="srv-card-img">
                    <Image
                      src={card.img}
                      alt={card.alt}
                      fill
                      sizes="(max-width: 900px) 72vw, 33vw"
                      style={{ objectFit: 'cover' }}
                    />
                    <span className="srv-card-num">{card.num}</span>
                  </div>
                  <div className="srv-card-body">
                    <h3 className="srv-card-title">{card.title}</h3>
                    <p className="srv-card-desc">{card.desc}</p>
                    <span className="srv-card-arrow">{card.cta}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* 06 BIG TYPE */}
        <div className="scene" id="s5">
          <div className="type-stage">
            <div className="type-head fade-up" style={d('0ms')}>
              <span className="num">06 / 09</span>
              <span className="label">De ce Buchetul Simonei</span>
            </div>
            <h2 className="type-mega lines">
              <span className="line"><span style={d('100ms')}>Calitate <em>în</em></span></span>
              <span className="line"><span style={d('300ms')}>fiecare</span></span>
              <span className="line"><span style={d('500ms')}><span className="stroke">petală</span>.</span></span>
            </h2>
            <div className="type-foot">
              <p className="type-desc fade-up" style={d('800ms')}>
                Folosim exclusiv flori proaspete, selectate cu grijă. Livrăm rapid, cu atenție și cu inimă — pentru că un buchet nu e doar un cadou, ci o emoție împachetată.
              </p>
              <Link className="type-cta fade-up" href="/shop" style={d('950ms')}>
                Cumpără Acum
              </Link>
            </div>
          </div>
        </div>

        {/* 07 STATS */}
        <div className="scene" id="s6">
          <div className="stats-stage">
            <div className="stats-head">
              <span className="stats-eyebrow fade-up" style={d('0ms')}>Cu cifrele jos</span>
              <h2 className="stats-title lines">
                <span className="line"><span style={d('100ms')}>Încrederea care</span></span>
                <span className="line"><span style={d('280ms')}>crește cu fiecare <em>buchet</em>.</span></span>
              </h2>
            </div>
            <div className="stats-grid">
              {[
                { delay: '500ms', num: '500+', label: 'Clienți fericiți', desc: 'Familii, cupluri și companii care ne aleg de fiecare dată când contează.' },
                { delay: '650ms', num: '24h',   label: 'Livrare garantată', desc: 'De la momentul comenzii până în mâinile tale — flori care încă respiră.' },
                { delay: '800ms', num: '100%',  label: 'Flori premium', desc: 'Selectate manual, fără compromisuri. Fiecare petală trecută printr-un control de calitate.' },
              ].map((stat) => (
                <div key={stat.label} className="stat-cell fade-up" style={d(stat.delay)}>
                  <span className="stat-num"><em>{stat.num}</em></span>
                  <span className="stat-label">{stat.label}</span>
                  <p className="stat-desc">{stat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 07B GOOGLE REVIEWS */}
        <div className="scene" id="s7">
          <GoogleReviewsSection d={d} />
        </div>

        {/* 08 EVENTS */}
        <div className="scene" id="s8">
          <div className="ev-stage">
            <div className="ev-head">
              <div>
                <span className="eyebrow fade-up" style={d('0ms')}>Ocazii speciale</span>
                <h2 className="ev-title lines" style={{ marginTop: '18px' }}>
                  <span className="line"><span style={d('100ms')}>Momente</span></span>
                  <span className="line"><span style={d('280ms')}>de <em>Neuitat</em>.</span></span>
                </h2>
              </div>
              <p className="ev-meta fade-up" style={d('500ms')}>
                Indiferent de ocazie — celebrăm momentele tale cu flori potrivite și un design care vorbește pentru tine.
              </p>
            </div>
            <div className="ev-grid">
              {[
                { delay: '600ms', img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1764067790/a631f8ec-b16b-402a-9566-67749f6b33e5/il75wd9vjs0ee5uqzyce.webp', tag: 'Romantic', name: 'Ziua Îndrăgostiților' },
                { delay: '700ms', img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1763577654/a425b4f7-bfce-46f5-8e0f-f4241d3131b1/vszdn1n6lpsjbmlzt198.webp', tag: 'Celebrare', name: 'Nunți & Botezuri' },
                { delay: '800ms', img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1763581406/5da3f391-0d05-4fd9-a7d2-fb1a98614479/kc9bod1ii9pm2najso7s.webp', tag: 'Aniversare', name: 'Zile de Naștere' },
                { delay: '900ms', img: 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1770890540/A120a_di3rum.png', tag: 'Corporate', name: 'Evenimente Speciale' },
              ].map((ev) => (
                <div key={ev.tag} className="ev-card fade-up" style={d(ev.delay)}>
                  <div className="ev-img" style={{ backgroundImage: `url('${ev.img}')` }} />
                  <div className="ev-overlay">
                    <span className="ev-tag">{ev.tag}</span>
                    <h3 className="ev-name">{ev.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 09 CONTACT */}
        <div className="scene" id="s9">
          <div className="contact-stage">
            <h2 className="contact-mega lines">
              <span className="line"><span style={d('0ms')}>Trimite-ne</span></span>
              <span className="line"><span style={d('200ms')}>o <em>comandă</em>.</span></span>
            </h2>
            <p className="contact-sub fade-up" style={d('500ms')}>
              Spune-ne despre momentul tău — noi îl traducem în flori.
            </p>
            <div className="contact-grid">
              <div className="cblock fade-up" style={d('650ms')}>
                <span className="cblock-label">Adresă</span>
                <p className="cblock-text">Str. Unirii 240<br />Tamaseni, Neamț</p>
              </div>
              <div className="cblock fade-up" style={d('750ms')}>
                <span className="cblock-label">Program</span>
                <p className="cblock-text">
                  {scheduleLines.map((line, i) => (
                    <span key={line}>{i > 0 && <br />}{line}</span>
                  ))}
                </p>
              </div>
              <div className="cblock fade-up" style={d('850ms')}>
                <span className="cblock-label">Livrare</span>
                <p className="cblock-text">Maxim 24h<br />de la comandă</p>
              </div>
            </div>
            <div className="contact-cta-row fade-up" style={d('1000ms')}>
              <Link className="btn-rose" href="/shop">Cumpără Acum</Link>
              <Link className="btn-outline" href="/contact">Contactează-ne</Link>
            </div>
            <div className="footer-line fade-up" style={d('1150ms')}>
              <span>© 2026 Buchetul Simonei. Toate drepturile rezervate.</span>
              <div className="footer-links">
                <Link href="/termeni-conditii">Termeni</Link>
                <Link href="/gdpr">GDPR</Link>
                <Link href="/politica-cookies">Cookies</Link>
              </div>
            </div>
          </div>
        </div>

      </div>{/* /stage */}
    </>
  );
}
